import { getCountryName } from "~/lib/helpers/constants";
import WorkspaceFinder from "../Home_Page/WorkspaceFinder";
import WhyOfficeHub from "../Home_Page/WhyOfficeHub";
import ModernHeroSection from "./ModernHeroSection";
import AmenitiesSection from "./AmenitiesSection";
import Footer from "../Footer";
import Container from "~/components/atoms/Container";
import { GoogleReviews } from "../Home_Page/GoogleReviews/GoogleReviews";
import FAQs from "../FAQs/FAQs";
import React, { Suspense, useEffect, useMemo, useRef, useState, lazy } from "react";
import { Await, useNavigation, Link } from "@remix-run/react";
import Text from "~/components/atoms/Text";
import { Loader2 } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader } from "~/components/ui/drawer";
import { PropertyData, Workspace } from "../Geosearch/interfaces";
import NoResults from "../Geosearch/NoResult";
import SearchBar from "../Searchbars/AreaMobileSearchbar";
import PGridCard from "../Card/PGridCard";
import { listingSlug, propertySlug, buildingSlug, workspaceSlug } from "~/lib/helpers/slugHelper";
import { buildCardListingDisplayFromListing, getListingIsFeatured } from "~/lib/helpers/cardListingDisplay";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import MobileOfficeSearchBanner from "../common/MobileOfficeBanner";
import { ClientOnly } from "../common/client-only";
import CustomMap from "../Map/map.client";

const FilterDrawer = lazy(() => import("../Geosearch/MobileDrawer"));

interface MobileLayoutProps {
    countryName: string;
    region: string;
    loaderData: any;
    city?: string;
    onContinue?: () => void;
    onBack?: () => void;
    step?: number;
    isMobile?: boolean;
    faqs: any;
    // Geo search props
    query?: string;
    filterData?: any;
    callGeoSearch?: Function;
    callGeoSearchForDrawer?: Function;
    onDrawerApply?: Function;
    setFilterData?: Function;
    geoData?: any;
    selectedSort?: { label: string; value: string };
    setSelectedSort?: Function;
    pathname?: string;
    range?: any;
    onLoadMore?: () => void;
    hasMore?: boolean;
    loading?: boolean;
}

export default function MobileLayout({
    countryName,
    region,
    loaderData,
    city,
    onContinue,
    onBack,
    step = 1,
    isMobile = false,
    faqs,
    // Geo search props
    query = "",
    filterData = {},
    callGeoSearch = () => { },
    callGeoSearchForDrawer = () => { },
    onDrawerApply = () => { },
    setFilterData = () => { },
    geoData,
    selectedSort = { label: '', value: '' },
    setSelectedSort = () => { },
    pathname = "",
    range,
    onLoadMore,
    hasMore,
    loading,
}: MobileLayoutProps) {

    const [displayMode, setDisplayMode] = useState("list");
    const [isOpen, setIsOpen] = useState(false);
    const [rightWidth, setRightWidth] = useState(0);
    const [mapLoaded, setMapLoaded] = useState(false);
    const ref = useRef(null);
    const navigation = useNavigation();
    const rightContainerRef = useRef(null);



    const toggleDrawer: VoidFunction = () => {
        setIsOpen((prevState) => !prevState);
    };

    const data = geoData;

    const renderNearbyListings = useMemo(() => {
        if (data && data?.data?.autoReCalibratedData?.length) {
            let cards = data.data.autoReCalibratedData.map((b: PropertyData, index: number) => {
                const link = listingSlug(b as any);
                const propertyLink = propertySlug({
                    ...b,
                    id: b.propertyId,
                } as any);
                const buildingLink = b?.buildingData
                    ? buildingSlug(b.buildingData as any)
                    : "";
                const workspaceLink = b?.workspaceData
                    ? workspaceSlug(b.workspaceData as any)
                    : "";
                const cardComponent = <PGridCard
                    {...buildCardListingDisplayFromListing(b, { isProperty: true })}
                    buildingLink={b.buildingName && b.buildingId ? buildingLink : ''}
                    workspaceLink={workspaceLink}
                    link={link}
                    propertyLink={propertyLink}
                    loading={navigation.state === 'loading'}
                    key={`list-${b.id}`}
                    isFirstVisible={false}
                    cardClassName='w-full'
                    isPropertyPage={true}
                    isFeatured={getListingIsFeatured(b)}
                />

                return (
                    <React.Fragment key={`card-${b.id}`}>
                        {cardComponent}
                    </React.Fragment>
                );
            });
            return cards;
        }
    }, [data, displayMode, navigation.state]);

    const renderCards = useMemo(() => {
        if (data && data?.data?.data?.length) {
            let cards = data.data.data.map((b: PropertyData, index: number) => {
                const propertyLink = propertySlug(b as any);
                const buildingLink = b?.buildingData
                    ? buildingSlug(b.buildingData as any)
                    : "";
                const workspaceLink = b?.workspaceData
                    ? workspaceSlug(b.workspaceData as any)
                    : "";
                const cardComponent = <PGridCard
                    {...buildCardListingDisplayFromListing(b, { isProperty: true })}
                    buildingLink={buildingLink}
                    workspaceLink={workspaceLink}
                    link={propertyLink}
                    propertyLink={propertyLink}
                    loading={navigation.state === 'loading'}
                    key={`list-${b.id}`}
                    isFirstVisible={false}
                    cardClassName='w-full'
                    isPropertyPage={true}
                    isFeatured={getListingIsFeatured(b)}
                />

                return (
                    <React.Fragment key={`card-${b.id}`}>
                        {cardComponent}
                    </React.Fragment>
                );
            });
            return cards;
        } else if (data) {
            return (
                <>
                    <div className="col-span-full w-full mb-4">
                        <NoResults query={query} params={data.params} isMobile />
                        {data?.data?.autoReCalibratedData?.length ? (
                            <div className="mt-5">
                                <Text className='text-base font-semibold'>Other options in and around {city}</Text>
                            </div>
                        ) : null}
                    </div>
                    {renderNearbyListings}
                    {data?.data?.autoReCalibratedData?.length ? (
                        <div className="col-span-full w-full flex justify-center my-5">
                            <OhButton variant="destructive" className="mx-auto"
                                onClick={() => {
                                    callGeoSearch({ latitude: data.data.autoReCalibratedData[0].geolocationLat, longitude: data.data.autoReCalibratedData[0].geolocationLong, zoom: 14 })
                                }}>Explore All Options</OhButton>
                        </div>
                    ) : null}
                </>
            );
        }
        return null;
    }, [data, displayMode, navigation.state]);

    const hasListings = data && data?.data?.data?.length > 0;

    return (
        <div className="flex flex-col w-full justify-center montserrat">
            <ModernHeroSection
                countryName={countryName}
                region={region}
                loaderData={loaderData}
                city={city}
                onContinue={onContinue}
                onBack={onBack}
                step={step}
                isCampaignPage={true}
                isMobile={isMobile}
            />
            <WorkspaceFinder region={region} country={getCountryName(region)} />
            <WhyOfficeHub region={region} country={getCountryName(region)} isMobile={true} />

            {/* ========================================
                GEO SEARCH LISTINGS SECTION - MOBILE
                ======================================== */}
            {data && (
                <div className="relative" style={{ marginBottom: 28 }}>
                    {/* Filter Drawer */}
                    {isOpen ? (
                        <Suspense fallback={null}>
                            <FilterDrawer
                                open={isOpen}
                                setOpen={setIsOpen}
                                data={data}
                                filterData={filterData}
                                callGeoSearch={callGeoSearchForDrawer}
                                workspace={null as any}
                                onApply={onDrawerApply as VoidFunction}
                                maxPrice={range}
                            />
                        </Suspense>
                    ) : null}

                    <div className="flex">
                        {/* Left Section */}
                        <div className="transition-all duration-300 pt-[10px] min-h-[calc(100vh-136px)] min-w-[375px] w-full">

                            <Text className='text-xl font-semibold px-4 mt-4 mb-1'>
                                Office Spaces in {city || countryName}
                            </Text>

                            <SearchBar
                                toggleDrawer={toggleDrawer}
                                setWidth={setRightWidth}
                                rightWidth={rightWidth}
                                setDisplayMode={setDisplayMode}
                                filterData={filterData}
                                callGeoSearch={callGeoSearch}
                                setFilterData={setFilterData}
                                query={query}
                                workspace={null as any}
                                displayMode={displayMode}
                                setSelectedSort={setSelectedSort}
                                selectedSort={selectedSort as any}
                                setMapLoaded={setMapLoaded}
                                isCampaignPage={true}
                                sticky
                                stickyTopClassName='top-0 px-4'
                            />

                            <Drawer
                                open={mapLoaded && rightWidth > 0}
                                onOpenChange={(open) => {
                                    if (!open) {
                                        setRightWidth(0);
                                        setDisplayMode('list');
                                    }
                                }}
                            >
                                <DrawerContent className='h-[90dvh] max-h-[90dvh] p-0 flex flex-col overflow-hidden'>
                                    <DrawerHeader className='px-4 border-b'>
                                        Map view
                                    </DrawerHeader>
                                    <div
                                        ref={rightContainerRef}
                                        className='flex-1 min-h-0 relative'
                                    >
                                        <Suspense
                                            fallback={
                                                <div className='h-full w-full flex items-center justify-center'>
                                                    Loading map...
                                                </div>
                                            }
                                        >
                                            <ClientOnly fallback={<div id='skeleton' />}>
                                                {() => (
                                                    <CustomMap
                                                        data={data.data.data}
                                                        totalListings={data.data.totalProperties}
                                                        rightWidth={rightWidth}
                                                        rightContainerRef={rightContainerRef}
                                                        width={rightWidth}
                                                        setWidth={setRightWidth}
                                                        handlePageChange={onLoadMore}
                                                        loading={navigation.state === 'loading'}
                                                        callGeoSearch={callGeoSearch}
                                                        displayMode={displayMode}
                                                        nearbyListings={data.data.autoReCalibratedData}
                                                        mobile
                                                        isPropertyCard
                                                        showPerDesk={true}
                                                        isLoadMore={onLoadMore}
                                                    />
                                                )}
                                            </ClientOnly>
                                        </Suspense>
                                    </div>
                                </DrawerContent>
                            </Drawer>

                            {hasListings ? (
                                <Text className='text-base px-4 mb-1 mt-1 text-gray-800'>
                                    Showing 1 - {data.data.visibleProperties || data.data.data.length} out of {data.data.totalProperties || data.data.totalListings} spaces
                                </Text>
                            ) : null}

                            <div
                                ref={ref}
                                id="campaign-listings-container"
                                className='mt-2'
                            >
                                <div className="grid grid-cols-1 gap-3 px-4 pb-4">
                                    {renderCards}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View More Button */}
                    {hasListings && hasMore && (
                        <div className="flex justify-center w-full my-4 px-4">
                            <OhButton
                                className="w-full justify-center"
                                onClick={onLoadMore}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Loading...
                                    </>
                                ) : (
                                    "Load more"
                                )}
                            </OhButton>
                        </div>
                    )}
                </div>
            )}

            <AmenitiesSection region={region} />
            <div id="reviews">
                <Container htmlFor="ultra-wide" className='px-4'>
                    <GoogleReviews country={getCountryName(region)} region={region} isMobile={true} hideViewAllReviews={true} />
                </Container>
            </div>
            <Suspense fallback={<div className="h-40 w-full bg-gray-50 rounded-lg" />}>
                <Await resolve={faqs}>
                    {(resolvedFaqs) => <FAQs faqs={resolvedFaqs} title="Frequently Asked Questions" />}
                </Await>
            </Suspense>
            <div className="pt-12">
                <MobileOfficeSearchBanner isCampaignPage={true} country={region} description={`Tell us what you need and we’ll shortlist the best available offices across ${city}, saving you days of research and back-and-forth.`} />
            </div>
            <Footer region={region} isMobile={true} isCampaignPage={true} />
        </div >
    );
}
